import { useState, type FormEvent } from "react";
import { invalidateApiCache } from "../../../app/api/cache";

interface LoginFormProps {
  onSuccess: () => void | Promise<void>;
}

const apiBaseUrl = import.meta.env.VITE_API_URL || "";

export function LoginForm({ onSuccess }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const res = await fetch(`${apiBaseUrl}/api/auth/sign-in/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json().catch(() => null);

      if (!res.ok || result?.error) {
        setError(result?.error?.message ?? result?.error ?? "Login failed");
        return;
      }

      invalidateApiCache();
      await onSuccess();
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-md">
      <div>
        <label
          className="block font-label-sm text-secondary mb-xs"
          htmlFor="login-email"
        >
          Email
        </label>
        <input
          id="login-email"
          className="w-full bg-surface-container border border-outline-variant rounded px-3 py-2 font-body-md text-on-surface placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
      </div>

      <div>
        <label
          className="block font-label-sm text-secondary mb-xs"
          htmlFor="login-password"
        >
          Password
        </label>
        <input
          id="login-password"
          className="w-full bg-surface-container border border-outline-variant rounded px-3 py-2 font-body-md text-on-surface placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          autoComplete="current-password"
        />
      </div>

      {error && (
        <div className="font-body-md text-error bg-error-container rounded px-3 py-2">
          {error}
        </div>
      )}

      <button
        type="submit"
        className="w-full bg-primary text-on-primary py-2.5 rounded font-label-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mt-sm"
        disabled={isLoading}
      >
        {isLoading ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
